/**
 * School Timezone Context Hook
 * Provides school timezone context for period and attendance components
 */

import { useTenant } from '../contexts/TenantContext';
import { 
  convertPeriodTimeToSchoolTimezone, 
  convertPeriodTimeToUserTimezone,
  formatPeriodTimeWithTimezone,
  getCurrentPeriodInSchoolTimezone,
  getNextPeriodInSchoolTimezone
} from '../utils/periodTimezoneUtils';

/**
 * Hook to get school timezone context and timezone-aware period utilities
 * @returns {object} School timezone context and utilities
 */
export const useSchoolTimezone = () => {
  const { currentSchool } = useTenant();
  
  const schoolTimezone = currentSchool?.timezone || 'UTC';
  const hasSchoolTimezone = !!currentSchool?.timezone;
  
  return {
    // Context
    schoolTimezone,
    hasSchoolTimezone,
    currentSchool,
    
    // Conversion utilities
    convertToSchoolTime: (timeString, userTimezone = null) => 
      convertPeriodTimeToSchoolTimezone(timeString, schoolTimezone, userTimezone),
      
    convertToUserTime: (timeString, userTimezone = null) => 
      convertPeriodTimeToUserTimezone(timeString, schoolTimezone, userTimezone),
    
    // Display utilities  
    formatTimeWithTimezone: (timeString, options = {}) => 
      formatPeriodTimeWithTimezone(timeString, schoolTimezone, options),
    
    // Period utilities
    getCurrentPeriod: (periods) => 
      getCurrentPeriodInSchoolTimezone(periods, schoolTimezone),
      
    getNextPeriod: (periods) => 
      getNextPeriodInSchoolTimezone(periods, schoolTimezone),
    
    // Time display with timezone awareness
    displayPeriodTime: (startTime, endTime, options = {}) => {
      if (!startTime || !endTime) return 'No time set';
      
      // Helper function to normalize time format (remove seconds if present)
      const normalizeTime = (timeString) => {
        if (!timeString) return '';
        // Convert "09:00:00" to "09:00" or keep "09:00" as is
        return timeString.split(':').slice(0, 2).join(':');
      };
      
      // Normalize the database time format
      const normalizedStartTime = normalizeTime(startTime);
      const normalizedEndTime = normalizeTime(endTime);
      
      console.log('🔍 Period time display debug:', {
        originalStart: startTime,
        originalEnd: endTime,
        normalizedStart: normalizedStartTime,
        normalizedEnd: normalizedEndTime,
        schoolTimezone
      });
      
      // Convert database times (stored in school timezone) to user's display timezone
      const convertedStartTime = convertPeriodTimeToUserTimezone(normalizedStartTime, schoolTimezone);
      const convertedEndTime = convertPeriodTimeToUserTimezone(normalizedEndTime, schoolTimezone);
      
      console.log('🔍 After timezone conversion:', {
        convertedStart: convertedStartTime,
        convertedEnd: convertedEndTime
      });
      
      // Convert to 12-hour format for display
      const formatTime = (timeString) => {
        if (!timeString || !timeString.includes(':')) {
          console.warn('Invalid time format for formatting:', timeString);
          return timeString;
        }
        
        // Check if it's already in 12-hour format
        if (timeString.match(/\d+:\d+[AP]M$/i)) {
          return timeString; // Already formatted
        }
        
        const [hours, minutes] = timeString.split(':').map(Number);
        
        if (isNaN(hours) || isNaN(minutes)) {
          console.warn('Invalid time format:', timeString);
          return timeString;
        }
        
        let hour12 = hours;
        const isPM = hours >= 12;
        
        if (hours > 12) {
          hour12 = hours - 12;
        } else if (hours === 0) {
          hour12 = 12;
        }
        
        return `${hour12}:${minutes.toString().padStart(2, '0')}${isPM ? 'PM' : 'AM'}`;
      };
      
      const formattedStart = formatTime(convertedStartTime);
      const formattedEnd = formatTime(convertedEndTime);
      
      console.log('🔍 Final formatted times:', {
        formattedStart,
        formattedEnd
      });
      
      return `${formattedStart} - ${formattedEnd}`;
    },
    
    // Enhanced period display with timezone context
    displayPeriodDetails: (period, options = {}) => {
      if (!period) return null;
      
      const {
        showDuration = false,
        showTimezoneInfo = false,
        format12Hour = true
      } = options;
      
      const timeDisplay = formatPeriodTimeWithTimezone(
        `${period.start_time} - ${period.end_time}`,
        schoolTimezone,
        { format12Hour, showTimezoneLabel: showTimezoneInfo }
      );
      
      if (showDuration) {
        const duration = calculatePeriodDuration(period.start_time, period.end_time);
        return `${timeDisplay} (${duration})`;
      }
      
      return timeDisplay;
    }
  };
};

/**
 * Calculate period duration 
 * @param {string} startTime - Start time in HH:MM or HH:MM:SS format
 * @param {string} endTime - End time in HH:MM or HH:MM:SS format  
 * @returns {string} - Duration in "Xh Ym" format
 */
const calculatePeriodDuration = (startTime, endTime) => {
  try {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch (error) {
    console.warn('Error calculating period duration:', error);
    return '';
  }
};
